import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { levelInfo, type DiagnosticLevel } from '@/data/diagnosticQuestions';
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  ClipboardList,
  Lightbulb,
  Route,
  Sparkles,
  Stethoscope,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LEVELS: DiagnosticLevel[] = [
  'iniciador',
  'explorador',
  'implementador',
  'integrador',
  'estratega',
  'transformador',
];

const OUTCOMES = [
  {
    icon: TrendingUp,
    title: 'Tu nivel de madurez en IA',
    description: 'Descubre en cuál de los 6 niveles te encuentras hoy y qué caracteriza tu forma de aplicar la IA.',
  },
  {
    icon: Brain,
    title: 'Evaluación personalizada con IA',
    description: 'Tus respuestas se analizan según su profundidad, experiencia práctica y visión estratégica.',
  },
  {
    icon: Target,
    title: 'Fortalezas y oportunidades',
    description: 'Reconoce las capacidades que ya dominas y los aspectos que puedes desarrollar para avanzar.',
  },
  {
    icon: Route,
    title: 'Recomendaciones de aprendizaje',
    description: 'Recibe una orientación acorde con tu nivel para seguir construyendo capacidades en inteligencia artificial.',
  },
];

const PublicDiagnosticPage = () => {
  const navigate = useNavigate();

  const authPath = (signup = false) =>
    `/auth?${signup ? 'mode=signup&' : ''}next=${encodeURIComponent('/diagnostic')}`;

  return (
    <div className="min-h-screen">
      <SEO
        title="Diagnóstico Personal de Madurez en IA — LÍDER IA"
        description="Descubre tu nivel personal de madurez y liderazgo en inteligencia artificial con una evaluación personalizada y recomendaciones para avanzar."
        path="/diagnostic"
      />

      <section className="px-4 sm:px-6 pt-10 pb-12 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
          <div className="space-y-6">
            <Badge variant="secondary" className="gap-1.5">
              <Stethoscope className="w-3.5 h-3.5" />
              Para ti
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              Descubre tu nivel de liderazgo y madurez en inteligencia artificial
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              El Diagnóstico Personal LÍDER IA analiza cómo comprendes, aplicas y lideras la IA para mostrarte dónde estás y cuál es tu siguiente paso.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="gap-2" onClick={() => navigate(authPath(true))}>
                Realizar diagnóstico gratis
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate(authPath())}>
                Ya tengo cuenta
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Primero crea tu cuenta. Después responderás 15 preguntas y recibirás tu resultado personalizado.
            </p>
          </div>

          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardHeader>
              <CardTitle className="text-lg">Tu recorrido de madurez</CardTitle>
              <p className="text-sm text-muted-foreground">Un ejemplo de los 6 niveles que puede identificar tu evaluación.</p>
            </CardHeader>
            <CardContent>
              <div className="relative space-y-3">
                <div className="absolute left-[1.15rem] top-5 bottom-5 w-px bg-border" />
                {LEVELS.map((level, index) => {
                  const info = levelInfo[level];
                  return (
                    <div key={level} className="relative flex items-center gap-3">
                      <div className="relative z-10 w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center shrink-0">
                        {index}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground">{info.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{info.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground text-center mt-5">
                La evaluación identifica tu nivel según la profundidad de tus respuestas.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-4 sm:px-6 py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto space-y-7">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">Qué obtendrás</h2>
            <p className="text-muted-foreground">Una lectura personal de tus capacidades para avanzar con claridad.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {OUTCOMES.map((outcome) => {
              const Icon = outcome.icon;
              return (
                <Card key={outcome.title} className="border-border/50">
                  <CardHeader>
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-base">{outcome.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{outcome.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 py-12">
        <div className="max-w-4xl mx-auto space-y-7">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">Cómo funciona</h2>
            <p className="text-muted-foreground">Tres pasos para conocer tu punto de partida.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: ClipboardList, title: 'Crea tu cuenta', text: 'Regístrate gratis para guardar tu información y acceder al diagnóstico.' },
              { icon: Lightbulb, title: 'Responde 15 preguntas', text: 'Comparte tu experiencia, uso práctico y visión sobre la inteligencia artificial.' },
              { icon: CheckCircle2, title: 'Conoce tu resultado', text: 'Recibe tu nivel de madurez, una evaluación personalizada y orientación para avanzar.' },
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-primary mb-1">PASO {index + 1}</p>
                    <h3 className="font-semibold text-foreground">{step.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{step.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 pb-16">
        <div className="max-w-4xl mx-auto">
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardContent className="py-8 text-center space-y-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Tu siguiente paso empieza aquí
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-foreground">Conoce hoy tu nivel de madurez en IA</h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Regístrate gratis, completa la evaluación y recibe un resultado construido a partir de tu experiencia real.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="gap-2" onClick={() => navigate(authPath(true))}>
                Realizar diagnóstico gratis
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate(authPath())}>
                Ya tengo cuenta
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
      </section>
    </div>
  );
};

export default PublicDiagnosticPage;
